import { useAuth } from '../contexts/AuthContext'
import { API_BASE_URL } from '../config'

export type DocumentTemplateStatus = 'draft' | 'published'

export interface MergeFieldDef {
  key: string
  label: string
}

export interface DocumentTemplate {
  id: string
  tenantId: string
  name: string
  documentType: string
  matterTypes: string[] | null
  partyRoles: string[]
  contentMarkdown: string
  mergeFields: MergeFieldDef[]
  version: number
  status: DocumentTemplateStatus
  forkedFromId: string | null
  createdAt: string
  updatedAt: string
  publishedAt?: string
}

export interface NewDocumentTemplate {
  name: string
  documentType: string
  matterTypes?: string[] | null
  partyRoles: string[]
  contentMarkdown: string
  mergeFields?: MergeFieldDef[]
  forkedFromId?: string
}

export interface DocumentTemplateEdit {
  name?: string
  contentMarkdown?: string
  mergeFields?: MergeFieldDef[]
  partyRoles?: string[]
}

export interface TemplatePreview {
  bodyPdfBase64: string
  executionPdfBase64: string
}

export type GenerationJobStatus = 'pending' | 'complete' | 'failed'

export interface GenerationJob {
  id: string
  matterId: string
  templateId: string
  status: GenerationJobStatus
  error?: string
  documentId?: string
}

// Authenticated: tenant-side document template library + per-matter
// generation. Mirrors documentService.ts/esignService.ts conventions.
export const useDocumentTemplateService = () => {
  const { token, authFetch } = useAuth()

  const listTemplates = async (): Promise<DocumentTemplate[]> => {
    if (!token) return []

    const response = await authFetch(`${API_BASE_URL}/document-templates`)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to list document templates: ${response.status}`)
    }

    return data.templates || []
  }

  const getTemplate = async (templateId: string): Promise<DocumentTemplate> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/document-templates/${templateId}`)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to get document template: ${response.status}`)
    }

    return data.template
  }

  const createTemplate = async (template: NewDocumentTemplate): Promise<DocumentTemplate> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/document-templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(template),
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to create document template: ${response.status}`)
    }

    return data.template
  }

  // Editing a SYSTEM-owned template forks a new tenant-owned copy — the
  // returned template's id may differ from templateId in that case (see
  // backend/src/document-templates.ts).
  const updateTemplate = async (templateId: string, edit: DocumentTemplateEdit): Promise<DocumentTemplate> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/document-templates/${templateId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(edit),
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to update document template: ${response.status}`)
    }

    return data.template
  }

  const publishTemplate = async (templateId: string): Promise<void> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/document-templates/${templateId}/publish`, {
      method: 'POST',
    })

    if (!response.ok) {
      const data = await response.json().catch(() => null)
      throw new Error(data?.error || `Failed to publish document template: ${response.status}`)
    }
  }

  const previewTemplate = async (templateId: string): Promise<TemplatePreview> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/document-templates/${templateId}/preview`, {
      method: 'POST',
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to render preview: ${response.status}`)
    }

    return data
  }

  // Best-effort, same enqueue-then-poll shape as documentService.ts's
  // fetchGovernmentReports/fetchPepFepCheck: this only starts the render (see
  // backend/src/document-generation-worker.ts) and returns a jobId
  // immediately. Poll getGenerationJobStatus for the outcome.
  const generateDocument = async (matterId: string, templateId: string): Promise<{ jobId: string }> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/generate-document`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ templateId }),
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to start document generation: ${response.status}`)
    }

    return data
  }

  const getGenerationJobStatus = async (matterId: string, jobId: string): Promise<GenerationJob> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/generate-document/${jobId}`)

    // A Gateway-level timeout/error can come back as plain text/HTML rather
    // than JSON, so this can't assume response.json() will succeed even when
    // !response.ok.
    let data: any = null
    try {
      data = await response.json()
    } catch {
      // fall through to the generic error below
    }

    if (!response.ok) {
      throw new Error(data?.error || `Failed to get document generation status: ${response.status}`)
    }

    return data
  }

  return {
    listTemplates,
    getTemplate,
    createTemplate,
    updateTemplate,
    publishTemplate,
    previewTemplate,
    generateDocument,
    getGenerationJobStatus,
  }
}
